import React from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import { propertyMarkerIcon, userLocationIcon } from "@/lib/mapIcons";
import { useLocale } from "@/contexts/LocaleContext";
import { photoUrl } from "@/lib/api";
import { BedDouble, Ruler, MapPin } from "lucide-react";

// Center: Dominican Republic
const DR_CENTER = [18.7357, -70.1627];
const DR_ZOOM = 8;

function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.flyTo(points[0], 13);
      return;
    }
    const lats = points.map((p) => p[0]);
    const lngs = points.map((p) => p[1]);
    const bounds = [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ];
    map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [JSON.stringify(points), map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function MapView({
  properties,
  height = "70vh",
  userLocation = null, // { lat, lng }
  radiusKm = null,
}) {
  const { t, formatPrice } = useLocale();
  const withCoords = (properties || []).filter(
    (p) => typeof p.latitude === "number" && typeof p.longitude === "number"
  );

  // Auto-fit bounds: user + property markers
  const boundsPoints = [
    ...withCoords.map((p) => [p.latitude, p.longitude]),
    ...(userLocation ? [[userLocation.lat, userLocation.lng]] : []),
  ];

  let center = DR_CENTER;
  let zoom = DR_ZOOM;
  if (userLocation) {
    center = [userLocation.lat, userLocation.lng];
    zoom = 12;
  } else if (withCoords.length > 0) {
    const avgLat = withCoords.reduce((s, p) => s + p.latitude, 0) / withCoords.length;
    const avgLng = withCoords.reduce((s, p) => s + p.longitude, 0) / withCoords.length;
    center = [avgLat, avgLng];
    zoom = withCoords.length === 1 ? 13 : 9;
  }

  return (
    <div className="relative rounded-lg overflow-hidden border border-border" style={{ height }} data-testid="map-view">
      <MapContainer center={center} zoom={zoom} scrollWheelZoom={true} className="h-full w-full">
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={boundsPoints} />

        {userLocation && (
          <>
            <Marker position={[userLocation.lat, userLocation.lng]} icon={userLocationIcon} />
            {radiusKm ? (
              <Circle
                center={[userLocation.lat, userLocation.lng]}
                radius={radiusKm * 1000}
                pathOptions={{
                  color: "#134074",
                  fillColor: "#8DA9C4",
                  fillOpacity: 0.12,
                  weight: 1.5,
                }}
              />
            ) : null}
          </>
        )}

        {withCoords.map((p) => (
          <Marker
            key={p.property_id}
            position={[p.latitude, p.longitude]}
            icon={propertyMarkerIcon}
          >
            <Popup>
              <div className="w-56" data-testid={`map-popup-${p.property_id}`}>
                {p.photos?.[0] && (
                  <div className="aspect-[4/3] overflow-hidden rounded mb-2">
                    <img src={photoUrl(p.photos[0])} alt="" className="h-full w-full object-cover" />
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-widest text-secondary">
                  {t(`types.${p.property_type}`)}
                </div>
                <h3 className="font-serif text-lg leading-tight mt-1">{p.title}</h3>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> <span className="truncate">{p.address}</span>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-foreground/80">
                  <span className="inline-flex items-center gap-1">
                    <BedDouble className="h-3.5 w-3.5" /> {p.bedrooms}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Ruler className="h-3.5 w-3.5" /> {p.square_meters} m²
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-serif text-lg text-primary">{formatPrice(p.price, p.currency)}</span>
                  <Link
                    to={`/properties/${p.property_id}`}
                    className="text-xs text-primary hover:underline underline-offset-4"
                    data-testid={`map-popup-link-${p.property_id}`}
                  >
                    Ver detalle →
                  </Link>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
